import { useCallback, useState } from "react";

import instance from "@store/middleware/api";
import { useAppDispatch } from "@store/hooks";
import { openReactivationCheckout } from "@store/reducers/cart";

/**
 * Open the standard subscription checkout, prefilled with the account's own
 * (lapsed) configuration — the single action offered in the awaiting-reactivation
 * state (ToU §17.4): the paid period ended while the account was paused, so
 * continuing means buying a fresh cycle with full card + billing control.
 *
 * Shared so the three places that offer it can't drift: the Account Issues
 * modal's "Manage Plan", and both renders of the amber reactivate banner (global
 * header + in-page on the claims page), which go straight here rather than
 * routing through the modal first — there is nothing left to fix by then, so the
 * modal would only be a step in the way.
 *
 * `onOpened` runs after the cart is opened (e.g. to close the calling modal).
 * On failure `error` is set and `opening` released, so a caller can surface it.
 */
export const useOpenReactivationCheckout = (): {
  open: (onOpened?: () => void) => Promise<void>;
  opening: boolean;
  error: string | null;
} => {
  const dispatch = useAppDispatch();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(
    async (onOpened?: () => void) => {
      setOpening(true);
      setError(null);
      try {
        const { data } = await instance.get("/subscriptions/my");
        dispatch(
          openReactivationCheckout({
            channels: data?.channels ?? 1,
            claims: data?.claimsPerChannel ?? 10,
            billingPeriod: data?.billingPeriod === "annual" ? "annual" : "monthly",
          }),
        );
        onOpened?.();
      } catch (e: any) {
        setError(e?.response?.data?.message ?? "We couldn't open reactivation. Please try again.");
      } finally {
        // Always release, including on success: callers can outlive one open
        // (the modal stays mounted behind its animation), and a latched
        // `opening` would leave the button reading "Opening…" for good.
        setOpening(false);
      }
    },
    [dispatch],
  );

  return { open, opening, error };
};
